/* Collection grid: show which bulk quantity boxes actually hold a quantity.
 *
 * Dawn's quick-add-bulk puts a stepper on every card, posting `updates[]` straight to the cart.
 * On a 573-SKU wholesale grid that is the most useful control on the page, because a dealer fills
 * a basket across many SKUs rather than opening each product one at a time.
 *
 * ⚠️ THE PROBLEM IT SOLVES IS SCANNING, NOT ENTRY. Every box reads "0" at rest, so a filled box
 * looks identical to an empty one from two feet away. After typing quantities into six products
 * out of thirty-six, a dealer has no way to see what they have done without reading every cell.
 * This marks the ones holding a quantity, and nothing else.
 *
 * ⚠️ IT DOES NOT TOUCH THE CART OR THE VALUES. Presentation only: it adds and removes one class.
 * If it fails to load, the grid behaves exactly as Dawn intends and the cart is unaffected.
 *
 * Delegated from the document, because the grid is replaced wholesale when a filter or a sort is
 * applied and any listener bound to the original inputs would be discarded with them.
 */
(function () {
  "use strict";

  var FILLED = "is-filled";

  function mark(input) {
    var wrap = input.closest(".quantity");
    if (!wrap) return;
    var n = parseInt(input.value, 10);
    wrap.classList.toggle(FILLED, !isNaN(n) && n > 0);
  }

  function markAll(root) {
    var inputs = (root || document).querySelectorAll(".product-grid quick-add-bulk .quantity__input");
    Array.prototype.forEach.call(inputs, mark);
  }

  // `input` covers typing, `change` covers the stepper buttons and native spinners.
  document.addEventListener("input", function (e) {
    if (e.target && e.target.classList && e.target.classList.contains("quantity__input")) mark(e.target);
  });
  document.addEventListener("change", function (e) {
    if (e.target && e.target.classList && e.target.classList.contains("quantity__input")) mark(e.target);
  });

  function boot() { markAll(); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Filtering and sorting re-render the grid, and the cart can update the boxes after an add, so
  // re-mark whenever the subtree changes rather than only on load.
  var grid = document.querySelector(".product-grid");
  if (grid && window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; markAll(); });
    }).observe(grid.parentNode || grid, { childList: true, subtree: true });
  }
})();


/* The filter-bar result count duplicates the hero stat until a filter is applied, at which point
   it starts saying something different and useful. Toggled from the URL rather than from the DOM,
   because Dawn re-renders this whole region through the Sections API on every facet change and
   any class set on the old node is thrown away. */
(function () {
  function sync() {
    var filtering = /[?&]filter\./.test(window.location.search);
    document.querySelectorAll(".facets-container .product-count").forEach(function (el) {
      el.classList.toggle("is-filtering", filtering);
    });
  }
  document.addEventListener("DOMContentLoaded", sync);
  window.addEventListener("popstate", sync);
  // Dawn swaps the facet markup in without a navigation event, so watch for the replacement.
  var host = document.getElementById("main-collection-filters") || document.body;
  new MutationObserver(sync).observe(host, { childList: true, subtree: true });
  sync();
})();


/* ---- Grid / list view ------------------------------------------------------------------------
   Injected rather than templated because main-collection-product-grid is Dawn's stock section and
   is not forked into src/; adding the control in Liquid would mean adopting that whole file.

   ⚠️ DEFAULTS TO LIST WHEN THE PAGE HAS NO PHOTOGRAPHY. 1,584 of 6,388 products have no image and
   they arrive in runs, so all 36 cards on Honeywell page one are placeholders. A grid of identical
   grey squares is worse than useless there. An explicit choice by the dealer always wins and is
   remembered; the auto-default only applies until they choose.

   Re-runs after every facet change, because Dawn replaces this markup through the Sections API. */
(function () {
  var KEY = "bhx-collection-view";

  function grid() { return document.querySelector(".product-grid"); }

  function preferred() {
    var saved = null;
    try { saved = localStorage.getItem(KEY); } catch (e) {}
    if (saved === "list" || saved === "grid") return saved;
    var g = grid();
    if (!g) return "grid";
    var cards = g.querySelectorAll(".card-wrapper").length;
    var blank = g.querySelectorAll(".bhx-noimg").length;
    return cards && blank / cards > 0.5 ? "list" : "grid";
  }

  function apply(view) {
    var g = grid();
    if (g) g.classList.toggle("bhx-list", view === "list");
    document.querySelectorAll(".bhx-viewswitch button").forEach(function (b) {
      b.setAttribute("aria-pressed", String(b.dataset.view === view));
    });
  }

  function mount() {
    var bar = document.querySelector(".facets-container .product-count") ||
              document.querySelector(".facets-container");
    if (!bar || document.querySelector(".bhx-viewswitch")) return;
    var wrap = document.createElement("div");
    wrap.className = "bhx-viewswitch";
    wrap.setAttribute("role", "group");
    wrap.setAttribute("aria-label", "Product layout");
    ["grid", "list"].forEach(function (v) {
      var b = document.createElement("button");
      b.type = "button";
      b.dataset.view = v;
      b.textContent = v === "grid" ? "Grid" : "List";
      b.addEventListener("click", function () {
        try { localStorage.setItem(KEY, v); } catch (e) {}
        apply(v);
      });
      wrap.appendChild(b);
    });
    bar.parentNode.insertBefore(wrap, bar.nextSibling);
  }

  function sync() { mount(); apply(preferred()); }

  document.addEventListener("DOMContentLoaded", sync);
  var host = document.getElementById("ProductGridContainer") || document.body;
  new MutationObserver(function () { sync(); }).observe(host, { childList: true, subtree: true });
  sync();
})();


/* ---- Load more --------------------------------------------------------------------------------
   36 products a page is Shopify's ceiling for this section, not a choice: products_per_page lives
   in Dawn's stock section and its range steps in fours up to 36. Honeywell is 10 pages and
   Radians is 27, which is a lot of round trips for a dealer building one order.

   ⚠️ PROGRESSIVE ENHANCEMENT, NOT A REPLACEMENT. The numbered pagination stays in the HTML and is
   only hidden once this script has successfully mounted, so a browser without JS, and Google,
   still get real crawlable page links. The button is inserted, never templated, because
   main-collection-product-grid is Dawn's stock section and is not forked into src/.

   ⚠️ APPENDED ROWS MUST INHERIT THE VIEW. Grid vs list is a class on .product-grid itself rather
   than on each card, so appended items pick it up for free. The filled-quantity marker is not, so
   its observer is re-run below. */
(function () {
  var loading = false;

  function grid() { return document.querySelector(".product-grid"); }
  function pager() { return document.querySelector(".pagination-wrapper"); }

  /* ⚠️ SELECT THE NEXT LINK BY aria-label, NOT BY CLASS. In this Dawn build the NEXT arrow is
     emitted with class "pagination__item--prev", which is either a theme bug or a shared arrow
     partial; either way .pagination__item--next does not exist and matching on it found nothing,
     so the button never mounted. The accessible name is the only honest signal in the markup.
     Falls back to incrementing the current page number if that label ever changes. */
  function nextHref(scope) {
    var d = scope || document;
    var a = d.querySelector('.pagination a[aria-label="Next page"]');
    if (a && a.getAttribute("href")) return a.getAttribute("href");
    var cur = d.querySelector(".pagination__item--current");
    if (!cur) return null;
    var n = parseInt(cur.textContent.trim(), 10);
    if (!n) return null;
    var links = [].slice.call(d.querySelectorAll(".pagination a[href]"));
    for (var i = 0; i < links.length; i++) {
      if (parseInt(links[i].textContent.trim(), 10) === n + 1) return links[i].getAttribute("href");
    }
    return null;
  }

  function announce(msg) {
    var live = document.getElementById("bhx-loadmore-live");
    if (live) live.textContent = msg;
  }

  function mount() {
    var g = grid(), p = pager();
    if (!g || !p || document.querySelector(".bhx-loadmore")) return;
    if (!nextHref()) return;

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "bhx-btn bhx-btn--ghost bhx-loadmore";
    btn.textContent = "Load more";

    var live = document.createElement("p");
    live.id = "bhx-loadmore-live";
    live.className = "visually-hidden";
    live.setAttribute("aria-live", "polite");

    var wrap = document.createElement("div");
    wrap.className = "bhx-loadmore__wrap";
    wrap.appendChild(btn);
    wrap.appendChild(live);
    p.parentNode.insertBefore(wrap, p);
    p.hidden = true;

    btn.addEventListener("click", function () {
      if (loading) return;
      var url = nextHref();
      if (!url) return;
      loading = true;
      btn.disabled = true;
      btn.textContent = "Loading";
      fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var added = doc.querySelectorAll(".product-grid > li");
          var g2 = grid();
          added.forEach(function (li) { g2.appendChild(document.importNode(li, true)); });

          // Swap in the fetched pagination so the NEXT click knows where to go.
          var freshPager = doc.querySelector(".pagination-wrapper");
          var cur = pager();
          if (freshPager && cur) {
            cur.innerHTML = freshPager.innerHTML;
            cur.hidden = true;
          }
          announce(added.length + " more products loaded");

          if (nextHref()) {
            btn.disabled = false;
            btn.textContent = "Load more";
          } else {
            wrap.parentNode.removeChild(wrap);
          }
          loading = false;
          document.dispatchEvent(new CustomEvent("bhx:products-appended"));
        })
        .catch(function () {
          // Fall back to the real pagination rather than stranding the dealer on a dead button.
          btn.disabled = false;
          btn.textContent = "Load more";
          loading = false;
          var cur = pager();
          if (cur) cur.hidden = false;
          announce("Could not load more products");
        });
    });
  }

  document.addEventListener("DOMContentLoaded", mount);
  var host = document.getElementById("ProductGridContainer") || document.body;
  new MutationObserver(mount).observe(host, { childList: true, subtree: true });
  mount();
})();


/* ---- MOQ steps on the collection grid ---------------------------------------------------------
   The product page and both carts set min and step in Liquid, because those templates are ours.
   The grid's input is rendered by Dawn's quantity-input.liquid, which is not forked into src/ and
   hard-codes step from quantity_rule.increment, so the MOQ is published on <quick-add-bulk> as
   data-bhx-moq and applied here.

   ⚠️ SETTING step ALONE IS NOT ENOUGH. Dawn's plus and minus buttons call stepUp()/stepDown(),
   which respect the attribute, but a dealer can still TYPE 7 against a step of 10. The browser
   would mark it invalid and say nothing, and Dawn adds to cart with JS rather than a native form
   submit, so nothing would stop it. The value is rounded UP to the next whole multiple on change,
   never down, because rounding down would quietly order less than a dealer asked for. */
(function () {
  function applyOne(host) {
    var moq = parseInt(host.getAttribute("data-bhx-moq"), 10);
    if (!moq || moq < 2) return;
    var input = host.querySelector(".quantity__input");
    if (!input || input.dataset.bhxMoqBound === "1") return;
    input.dataset.bhxMoqBound = "1";
    input.step = String(moq);
    input.min = "0";
    input.addEventListener("change", function () {
      var v = parseInt(input.value, 10);
      if (!v || v < 0) return;
      if (v % moq !== 0) input.value = String(Math.ceil(v / moq) * moq);
    });
  }
  function apply() { document.querySelectorAll("quick-add-bulk[data-bhx-moq]").forEach(applyOne); }
  document.addEventListener("DOMContentLoaded", apply);
  document.addEventListener("bhx:products-appended", apply);
  var host = document.getElementById("ProductGridContainer") || document.body;
  new MutationObserver(apply).observe(host, { childList: true, subtree: true });
  apply();
})();


/* ---- Product-type rail: THE COLLAPSE IS GONE. It renders every type, always. -------------------
   Omar, 2026-09-07, on /collections/all: "there are safety glasses on the page and no filters for
   safety glasses that you can see."

   He was right, and the collapse was the cause. Each group clipped to ONE row, and the chips are
   ordered alphabetically, so Safety cut off after "Hi-Vis & Workwear" and everything from
   "Respirators & Masks" to "Safety Goggles" fell behind the toggle. Measured at 1440: 24 types
   exist, 13 showed. The page sorts A-Z, so the first products on screen were the safety glasses
   whose filter had just been hidden.

   ⚠️ THE HEIGHT ARGUMENT THAT JUSTIFIED THE COLLAPSE NO LONGER HOLDS. It was written to keep the
   first product from being pushed ~300px down. On the one page where the rail is long, the 480px
   knife-tag card band below it has now stood down (see bhx-collection-context.liquid), so showing
   all 24 types costs about two extra rows against 480px returned. The page ends up far shorter
   than it was, with nothing hidden.

   📌 An alphabetical truncation is the wrong shape of control ANYWHERE: what it hides is decided by
   the first letter of a label, which correlates with nothing a dealer wants. If this rail ever
   needs shortening again, order by product count and cut the tail, do not cut the alphabet.

   The toggle button, the --collapsed class and the max-height rule are all removed rather than
   left dormant, so nothing can switch this back on by accident. */


/* ---- Product-type rail: each GROUP is collapsible ---------------------------------------------
   Omar, 2026-09-07: "since that list is getting quite extensive now, each little category should
   be collapsible." The rail went from 24 types to 44 the same day, once Zippo and the knives were
   typed, and 44 chips is 413px at 1440.

   ⚠️ THIS IS THE OPPOSITE OF THE COLLAPSE DELETED ABOVE, not a reinstatement of it. That one cut
   each group mid-list on an alphabetical boundary, so a dealer saw seven of ten Safety chips with
   no way to tell that Safety Glasses and its 444 products were among the three missing. This
   collapses a group as a WHOLE, behind its own name and its own count, so the heading always
   states what is inside. Nothing is ever partially shown, which is the failure mode that mattered.

   📌 EVERY GROUP STARTS CLOSED, ON EVERY VIEWPORT, AND NOT FROM HERE. Omar, 2026-09-07, on
   /collections/all: "I would like the little product type tags at the top to all start in a
   closed state." The starting position is now rendered by bhx-collection-hero, which ships each
   group with bhx-chipgroup--collapsed and aria-expanded="false".

   This used to be decided here, by window width: open at 1440 where the whole rail is 413px and
   costs little, closed at 390 where the same chips run past a screen and bury the products. That
   width reasoning was sound and is simply no longer the question. What ruled out doing it in JS
   at all is that the page painted the rail OPEN and shut it a frame later, and on the catalogue
   page that flash is all four groups and 44 chips.

   ⚠️ SO THIS FILE NO LONGER SETS ANY DEFAULT. It wires the toggle and nothing else. A set(...)
   on load, at any width, brings the flash back. If the default itself needs to change, change
   the Liquid. */
(function () {
  var groups = document.querySelectorAll("[data-bhx-chipgroup]");
  Array.prototype.forEach.call(groups, function (group) {
    if (group.dataset.bhxGroupInit) return;
    group.dataset.bhxGroupInit = "1";

    var btn = group.querySelector(".bhx-chipgroup__label");
    var list = group.querySelector(".bhx-chips");
    if (!btn || !list) return;

    var set = function (open) {
      group.classList.toggle("bhx-chipgroup--collapsed", !open);
      btn.setAttribute("aria-expanded", String(open));
    };

    // ⚠️ NO DEFAULT IS APPLIED ON LOAD. THE MARKUP ALREADY CARRIES IT.
    // This read `set(window.innerWidth >= PHONE)`, i.e. open on desktop and closed on a phone.
    // Omar, 2026-09-07, wants every group closed everywhere, and the state moved into the Liquid
    // to get it: bhx-collection-hero renders each group with bhx-chipgroup--collapsed and
    // aria-expanded="false" already set.
    //
    // 🔴 DO NOT PUT A set(...) CALL BACK HERE. Whatever it passed would re-decide the state a
    // frame after paint, which is the flash the markup default exists to remove, and on
    // /collections/all that flash is all four groups and 44 chips. This function now only wires
    // the toggle; the starting position is the server's business.
    btn.addEventListener("click", function () {
      set(btn.getAttribute("aria-expanded") !== "true");
    });
  });
})();
